import { getSupabaseClient, isSupabaseConfigured } from './supabase';
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  email: string;
  created_at: string;
}

export interface AuthState {
  user: AuthUser | null;
  session: Session | null;
  loading: boolean;
}

export interface AuthError {
  code: string;
  message: string;
  actionable?: boolean;
  suggestion?: string;
  retryable?: boolean;
}

export interface AuthResponse {
  user: User | null;
  session: Session | null;
  success: boolean;
  message?: string;
}

export class AuthService {
  private getErrorDetails(error: any): AuthError {
    const errorMessage = error?.message || 'Unknown error occurred';
    const errorCode = error?.code || 'unknown';

    // Map common Supabase auth errors to user-friendly messages
    const errorMap: Record<string, AuthError> = {
      'invalid_credentials': {
        code: 'invalid_credentials',
        message: 'Invalid email or password',
        actionable: true,
        suggestion: 'Please check your credentials and try again',
        retryable: true
      },
      'email_not_confirmed': {
        code: 'email_not_confirmed',
        message: 'Please check your email and click the confirmation link',
        actionable: true,
        suggestion: 'Check your inbox and spam folder for the confirmation email',
        retryable: false
      },
      'signup_disabled': {
        code: 'signup_disabled',
        message: 'User registration is currently disabled',
        actionable: false,
        suggestion: 'Please contact support if you need to create an account',
        retryable: false
      },
      'too_many_requests': {
        code: 'too_many_requests',
        message: 'Too many attempts. Please wait a moment and try again',
        actionable: true,
        suggestion: 'Wait 60 seconds before attempting to sign in again',
        retryable: true
      },
      'user_not_found': {
        code: 'user_not_found',
        message: 'No account found with this email address',
        actionable: true,
        suggestion: 'Check your email address or create a new account',
        retryable: false
      },
      'weak_password': {
        code: 'weak_password',
        message: 'Password is too weak',
        actionable: true,
        suggestion: 'Use at least 8 characters with letters, numbers, and symbols',
        retryable: false
      },
      'email_address_invalid': {
        code: 'email_address_invalid',
        message: 'Please enter a valid email address',
        actionable: true,
        suggestion: 'Check your email format (e.g., user@example.com)',
        retryable: false
      },
      'password_should_be_at_least_6_characters': {
        code: 'password_too_short',
        message: 'Password must be at least 6 characters long',
        actionable: true,
        suggestion: 'Add more characters to meet the minimum length requirement',
        retryable: false
      }
    };

    // Check for exact matches first
    if (errorMap[errorCode]) {
      return errorMap[errorCode];
    }

    // Check for partial matches in error message
    for (const [key, errorInfo] of Object.entries(errorMap)) {
      if (errorMessage.toLowerCase().includes(key.replace(/_/g, ' '))) {
        return errorInfo;
      }
    }

    // Default error for unknown cases
    return {
      code: errorCode,
      message: errorMessage,
      actionable: false,
      suggestion: 'Please try again or contact support if the problem persists',
      retryable: true
    };
  }

  async signUp(email: string, password: string): Promise<AuthResponse> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`
      }
    });
    
    if (error) {
      throw this.getErrorDetails(error);
    }
    
    return {
      ...data,
      success: true,
      message: 'Account created successfully! Please check your email to confirm your account.'
    };
  }

  async signIn(email: string, password: string): Promise<AuthResponse> {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      throw this.getErrorDetails(error);
    }
    
    return {
      ...data,
      success: true,
      message: 'Successfully signed in!'
    };
  }

  async signOut() {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    
    const supabase = getSupabaseClient();
    
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw this.getErrorDetails(error);
    }
  }

  async getCurrentUser(): Promise<User | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }
    
    try {
      const supabase = getSupabaseClient();
      
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        return null;
      }
      return user;
    } catch (error) {
      return null;
    }
  }

  async getCurrentSession(): Promise<Session | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }
    
    try {
      const supabase = getSupabaseClient();
      
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        return null;
      }
      return session;
    } catch (error) {
      return null;
    }
  }

  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    if (!isSupabaseConfigured()) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    
    try {
      const supabase = getSupabaseClient();
      return supabase.auth.onAuthStateChange((event: AuthChangeEvent, session: Session | null) => {
        callback(event, session);
      });
    } catch (error) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
  }

  async resetPassword(email: string) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    
    const supabase = getSupabaseClient();
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    
    if (error) {
      throw this.getErrorDetails(error);
    }
  }

  async updatePassword(password: string) {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase is not configured. Please set up your environment variables.');
    }
    
    const supabase = getSupabaseClient();
    
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      throw this.getErrorDetails(error);
    }
  }
}
